import Validator from 'validatorjs';
import { memoize } from 'lodash';

import $clouds from 'app/stores/clouds/$clouds';
import GcpExportConfigFields from 'app/views/setup/tasks/cloud_v2/CloudExportConfigWizard/components/gcp/GcpExportConfigFields';

export const GCP_CLOUD_EXPORT_CONFIG_STEP = 'gcp-export-configuration';
export const GCP_CLOUD_PROVIDER_CONFIG_STEP = 'gcp-provider-configuration';
export const GCP_CLOUD_VERIFICATION_STEP = 'gcp-verification';
export const GCP_CLOUD_FINAL_STEP = 'gcp-final-steps';

export const GCP_EXPORT_ADDITIONAL_STEPS = [
  {
    id: GCP_CLOUD_EXPORT_CONFIG_STEP,
    title: 'Kentik GCP Export Configuration',
    component: GcpExportConfigFields,
    registerValidators: (form) => {
      Validator.registerAsync('unique_project_id', (value, args, attribute, passes) => {
        // Gcp project id must be unique across all of current kentik environment
        // Note that this check excludes current CID, so multiple exports with same project id are ok for same company
        const projectId = form.getValue('properties.gce_project');
        return $clouds
          .checkCloudProviderIdEligibility({
            cloudProvider: 'gcp',
            ids: [projectId]
          })
          .then((response) => {
            if (!response) {
              // $clouds.checkCloudProviderIdEligibility returns nothing, will only throw an error
              // no response means no error, all good to proceed
              passes(true);
            }
          })
          .catch((err) => {
            passes(false, err.message);
          });
      });

      Validator.registerAsync(
        'unique_gcp_service_account_email',
        function validateUniqueServiceAccountEmail(value, args, attribute, passes) {
          const { properties } = this.validator.input;
          const { kentik_gcp_service_account_id, gcp_api_access_method } = properties;

          // impersonation stores no service account record, so there is nothing to collide with
          if (gcp_api_access_method === 'service_account_impersonation') {
            return passes(true);
          }

          // when prev gcp service account is selected, not need to validate field
          if (kentik_gcp_service_account_id) {
            passes(true);
          }

          return $clouds
            .checkGcpServiceAccountEmailEligibility(value)
            .then((gcpServiceAccountModel) => {
              if (!gcpServiceAccountModel) {
                // $clouds.checkCloudProviderIdEligibility returns nothing, will only throw an error
                // no response means no error, all good to proceed
                passes(true);
              }

              passes(false, 'Service account email already exists. Please select from saved service accounts.');
            })
            .catch((err) => {
              passes(false, err.message);
            });
        }
      );
    },
    fieldsToRemove: ($cloudExportWizard) => {
      if ($cloudExportWizard.isFlowLogCollectionEnabled || $cloudExportWizard.isGcpCloudRunLogEnabled) {
        return [];
      }
      return ['properties.gce_subscription'].filter((optionalField) => $cloudExportWizard.formHasField(optionalField));
    },
    generateFields: memoize(
      () => ({
        'properties.gce_project': {
          label: 'Project ID',
          rules: 'required|unique_project_id'
        },
        'properties.gce_subscription': {
          label: 'Subscription',
          rules: 'required'
        },
        'properties.gcp_api_access_method': {
          defaultValue: 'kentik_service_account',
          rules: 'required_if:cloud_provider,gcp'
        },
        'properties.kentik_gcp_service_account_id': {
          defaultValue: null,
          rules: 'required_if:properties.gcp_api_access_method,service_account',
          transform: {
            out: (value) => value || null
          }
        },
        'properties.gcp_service_account_email': {
          label: 'Service Account Email',
          placeholder: 'Enter service account email',
          rules: [
            {
              // requiredIfMultiple is able to handle booleans unlike required_if out of the box
              requiredIfMultiple: ['cloud_provider', 'gcp', 'properties.gcp_api_access_method', 'service_account']
            },
            { requiredIfMultiple: ['properties.gcp_api_access_method', 'service_account_impersonation'] },
            'unique_gcp_service_account_email'
          ]
        },
        'properties.kentik_credential_secret_name': {
          label: 'Kentik Credential',
          helpText: 'Select the Kentik Credential that contains ssh key for service account email',
          rules: [
            {
              // requiredIfMultiple is able to handle booleans unlike required_if out of the box
              requiredIfMultiple: ['cloud_provider', 'gcp', 'properties.gcp_api_access_method', 'service_account']
            }
          ]
        }
      }),
      ($cloudExportWizard) => $cloudExportWizard.selectedCloudProvider
    )
  }
];
